<script setup>
// "Meu plano": uso real (billing/usage) de cada recurso auto-gerenciável +
// CARRINHO de upgrade — o cliente escolhe a quantidade de VÁRIOS recursos
// (caixas, usuários, disco) e paga tudo num PIX só (billing/purchase_cart).
//
// Preços de exibição seguem o MODELO NOVO (2026-07): caixa extra R$197/mês,
// usuário extra R$75/mês, disco +100 GB por R$20/mês, contatos +R$47 a cada
// 1.000. O valor cobrado de fato vem dos registros SubscriptionAddon (catálogo),
// que já estão nesses preços.
//
// Módulos de produto NÃO têm preço único: Meet/Campanhas/Marketing custam
// R$47/mês, Ligações custa R$197/mês (2026-08, valor único por conta, não é
// por dispositivo). Por isso o preço de cada módulo vem do catálogo
// (addonFor(mod.slug)?.monthly_price) em vez de uma constante fixa — só assim
// o carrinho soma o mesmo valor que o backend de fato cobra.
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import BillingAPI from 'dashboard/api/billing';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const NEW_PRICES = {
  inbox: 197,
  agent: 75,
  storage5gb: 47,
  contacts1k: 47,
};

// Módulos de produto (Meet/Campanhas/Marketing R$47/mês, Ligações R$197/mês):
// contas antigas têm tudo incluso (grandfather); novas ativam aqui pelo
// carrinho. Exceção: Ligações ficou de fora do grandfather — toda conta paga
// por ele, mesmo as antigas.
// Chave 'social_studio' (não 'social'): tem que bater com o `module_key` que
// Billing::ModuleAccess::MODULES e EnsuresProductModule usam — é essa mesma
// string que volta em addonsResponse.data.modules (Billing::ModuleAccess#summary)
// e no 402 (module_required) tratado pelo ModulePaywallModal.
const MODULES = [
  { key: 'meet', slug: 'modulo-meet', icon: 'i-lucide-video' },
  { key: 'campaigns', slug: 'modulo-campanhas', icon: 'i-lucide-megaphone' },
  { key: 'marketing', slug: 'modulo-marketing', icon: 'i-lucide-line-chart' },
  { key: 'calling', slug: 'modulo-ligacoes', icon: 'i-lucide-phone' },
  { key: 'social_studio', slug: 'modulo-social', icon: 'i-lucide-share-2' },
  { key: 'ai_studio', slug: 'modulo-ia', icon: 'i-lucide-brain' },
  { key: 'forms', slug: 'modulo-formularios', icon: 'i-lucide-file-text' },
  {
    key: 'comment_automations',
    slug: 'modulo-comentarios',
    icon: 'i-lucide-message-square-reply',
  },
  { key: 'pages', slug: 'modulo-paginas', icon: 'i-lucide-panels-top-left' },
];

const isLoading = ref(true);
const usage = ref(null);
const addonsCatalog = ref([]);
const modulesState = ref({});

const brl = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v || 0
  );

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [usageResponse, addonsResponse] = await Promise.all([
      BillingAPI.getUsage(),
      BillingAPI.getAddons(),
    ]);
    usage.value = usageResponse.data.usage;
    addonsCatalog.value = addonsResponse.data.addons || [];
    modulesState.value = addonsResponse.data.modules || {};
  } catch {
    usage.value = null;
    addonsCatalog.value = [];
  } finally {
    isLoading.value = false;
  }
};

const addonFor = slug => addonsCatalog.value.find(a => a.slug === slug);
// Preço real do módulo (catálogo) — cada módulo pode custar um valor
// diferente (Meet/Campanhas/Marketing R$47, Ligações R$197).
const modulePriceFor = mod => addonFor(mod.slug)?.monthly_price || 0;
const inboxAddon = computed(() => addonFor('extra-inbox'));
// Rede social extra do Estúdio Social (R$47/mês cada) — linha só aparece com o módulo ativo
const socialNetworkAddon = computed(() => addonFor('rede-social-extra'));
const agentAddon = computed(() => addonFor('extra-agent'));
// Add-on de disco — buscado pelo addon_type em vez do slug pra tolerar outros
// tamanhos no futuro.
const storageAddon = computed(() =>
  addonsCatalog.value.find(a => a.addon_type === 'storage')
);
// Add-on de contatos — pacote de +1.000 no limite da base (resource, R$47).
const contactsAddon = computed(() => addonFor('extra-contacts'));

const inboxUsage = computed(() => usage.value?.inboxes || null);
const agentUsage = computed(() => usage.value?.agents || null);
const contactsUsage = computed(() => usage.value?.contacts || null);
const storageUsage = computed(() => usage.value?.storage || null);
const socialUsage = computed(() => usage.value?.social_networks || null);

const storageUsedGb = computed(() =>
  storageUsage.value ? (storageUsage.value.current / 1024).toFixed(2) : '0'
);
const storageLimitGb = computed(() => {
  const limit = storageUsage.value?.limit;
  return limit ? (limit / 1024).toFixed(1) : null;
});

const meterPercent = usageObj => {
  if (!usageObj || !usageObj.limit) return 0;
  return Math.min(100, Math.round((usageObj.current / usageObj.limit) * 100));
};

const meterTone = percent => {
  if (percent >= 90) return 'bg-n-ruby-9';
  if (percent >= 70) return 'bg-n-amber-9';
  return 'bg-n-brand';
};

// ==========================================
// Carrinho de upgrade — escolhe VÁRIOS recursos e paga tudo num PIX só.
// ==========================================
const cart = reactive({
  inbox: 0,
  social_network: 0,
  agent: 0,
  storage: 0,
  contacts: 0,
  meet: 0,
  campaigns: 0,
  marketing: 0,
  calling: 0,
  social_studio: 0,
  ai_studio: 0,
  forms: 0,
  comment_automations: 0,
  pages: 0,
});
const moduleEnabled = key => modulesState.value?.[key]?.enabled === true;
// Origem do acesso: addon (contratou), override_on (bônus negociado pelo
// comercial — sem cobrança), grandfather (cliente da era tudo-incluso).
const moduleBadgeKey = key => {
  const source = modulesState.value?.[key]?.source;
  if (source === 'override_on') return 'BILLING.MY_PLAN.MODULE_BONUS';
  if (source === 'addon') return 'BILLING.MY_PLAN.MODULE_CONTRACTED';
  return 'BILLING.MY_PLAN.MODULE_ACTIVE';
};
const toggleModuleInCart = key => {
  cart[key] = cart[key] > 0 ? 0 : 1;
};
const isSubmitting = ref(false);

const modal = ref({ open: false, step: 'pix', invoice: null });
const billingForm = ref({ cpfCnpj: '', name: '', phone: '' });
const isSavingBilling = ref(false);
const pixCopied = ref(false);
let pollTimer = null;

const resourceAddonId = resource => {
  if (resource === 'inbox') return inboxAddon.value?.id;
  if (resource === 'agent') return agentAddon.value?.id;
  if (resource === 'storage') return storageAddon.value?.id;
  if (resource === 'contacts') return contactsAddon.value?.id;
  if (resource === 'social_network') return socialNetworkAddon.value?.id;
  const mod = MODULES.find(m => m.key === resource);
  if (mod) return addonFor(mod.slug)?.id;
  return null;
};

const setCart = (resource, n) => {
  cart[resource] = Math.max(0, Math.min(99, n || 0));
};

const modulesInCart = computed(() => MODULES.filter(m => cart[m.key] > 0));

const cartTotal = computed(
  () =>
    cart.inbox * NEW_PRICES.inbox +
    cart.agent * NEW_PRICES.agent +
    cart.storage * NEW_PRICES.storage5gb +
    cart.contacts * NEW_PRICES.contacts1k +
    cart.social_network * (socialNetworkAddon.value?.monthly_price || 47) +
    modulesInCart.value.reduce((sum, mod) => sum + modulePriceFor(mod), 0)
);
const cartCount = computed(
  () =>
    cart.inbox +
    cart.agent +
    cart.storage +
    cart.contacts +
    cart.social_network +
    modulesInCart.value.length
);

const CART_KEYS = [
  'inbox',
  'social_network',
  'agent',
  'storage',
  'contacts',
  'meet',
  'campaigns',
  'marketing',
  'calling',
  'social_studio',
  'ai_studio',
  'forms',
  'comment_automations',
  'pages',
];

const cartItems = computed(() =>
  CART_KEYS.filter(r => cart[r] > 0 && resourceAddonId(r)).map(r => ({
    addon_id: resourceAddonId(r),
    quantity: cart[r],
  }))
);

const resetCart = () => {
  CART_KEYS.forEach(k => {
    cart[k] = 0;
  });
};

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(async () => {
    if (!modal.value.invoice?.id) return;
    try {
      const { data } = await BillingAPI.getPaymentStatus(
        modal.value.invoice.id
      );
      if (data.status === 'paid') {
        stopPolling();
        modal.value.step = 'confirmed';
        useAlert(t('BILLING.MY_PLAN.ACTIVATED'));
        resetCart();
        await Promise.all([fetchAll(), store.dispatch('accounts/limits')]);
        setTimeout(() => {
          modal.value = { open: false, step: 'pix', invoice: null };
        }, 1800);
      }
    } catch {
      // silent poll failure — next tick retries
    }
  }, 4000);
};

// Cobra o carrinho inteiro num PIX só.
const checkout = async () => {
  if (!cartItems.value.length) return;
  isSubmitting.value = true;
  try {
    const { data } = await BillingAPI.purchaseCart(cartItems.value);
    // Cliente sem PIX/boleto paga no cartão: o backend devolve o checkout do
    // Stripe em vez do QR code.
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
      return;
    }
    if (data.needs_billing_info) {
      // 1ª compra: pede dados; o carrinho continua montado pra reenviar depois.
      modal.value = { open: true, step: 'billingInfo', invoice: null };
      return;
    }
    // Sem pró-rata: a compra libera os recursos na hora e o valor entra na
    // próxima fatura. Não há PIX para exibir — confirma, limpa o carrinho e
    // recarrega os limites (mesmo caminho do sucesso do polling antigo).
    if (data.activated) {
      modal.value = { open: false, step: 'pix', invoice: null };
      useAlert(t('BILLING.MY_PLAN.RELEASED_NOW'));
      resetCart();
      await Promise.all([fetchAll(), store.dispatch('accounts/limits')]);
      return;
    }
    if (data.invoice) {
      // Os add-ons já foram criados e ATIVADOS no backend — o carrinho cumpriu
      // o papel dele, mesmo com o PIX ainda por pagar. Sem limpar aqui, fechar
      // o modal e clicar "Comprar" de novo reenvia o mesmo carrinho e o cliente
      // fica com dois conjuntos de recursos e a mensalidade dobrada.
      // (needs_billing_info sai antes deste ponto: lá o carrinho PRECISA
      // continuar montado para reenviar depois de salvar os dados.)
      resetCart();
      modal.value = { open: true, step: 'pix', invoice: data.invoice };
      startPolling();
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING.MY_PLAN.PURCHASE_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const submitBillingInfo = async () => {
  const doc = (billingForm.value.cpfCnpj || '').replace(/\D/g, '');
  if (doc.length !== 11 && doc.length !== 14) {
    useAlert(t('BILLING.MY_PLAN.INVALID_DOC'));
    return;
  }
  isSavingBilling.value = true;
  try {
    await BillingAPI.saveFirstPaymentInfo({
      cpfCnpj: doc,
      name: billingForm.value.name,
      phone: billingForm.value.phone,
    });
    // Dados salvos: refaz o checkout do mesmo carrinho.
    await checkout();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING.MY_PLAN.BILLING_INFO_ERROR')
    );
  } finally {
    isSavingBilling.value = false;
  }
};

const copyPix = async () => {
  if (!modal.value.invoice?.pix_payload) return;
  try {
    await navigator.clipboard.writeText(modal.value.invoice.pix_payload);
    pixCopied.value = true;
    setTimeout(() => {
      pixCopied.value = false;
    }, 2000);
  } catch {
    // Clipboard unavailable; ignore.
  }
};

const closeModal = () => {
  stopPolling();
  modal.value = { open: false, step: 'pix', invoice: null };
  billingForm.value = { cpfCnpj: '', name: '', phone: '' };
};

onMounted(async () => {
  await fetchAll();
  // Veio do paywall (?module=meet|campaigns|marketing): módulo já entra no
  // carrinho pronto pra pagar.
  const wanted = route.query.module;
  if (
    wanted &&
    MODULES.some(mod => mod.key === wanted) &&
    !moduleEnabled(wanted)
  ) {
    cart[wanted] = 1;
  }
});
onUnmounted(stopPolling);
</script>

<template>
  <div class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden">
    <div class="px-6 py-4 border-b border-n-weak bg-n-alpha-1">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('BILLING.MY_PLAN.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11 mt-0.5">
        {{ t('BILLING.MY_PLAN.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-n-slate-10" />
    </div>

    <div v-else class="divide-y divide-n-weak">
      <!-- Caixas -->
      <div class="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5">
            <span class="i-lucide-inbox w-4 h-4 text-n-slate-10 shrink-0" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BILLING.MY_PLAN.INBOXES') }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums ml-auto sm:ml-0">
              {{ inboxUsage?.current ?? 0 }} /
              {{ inboxUsage?.limit ?? t('BILLING.MY_PLAN.UNLIMITED') }}
            </span>
          </div>
          <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
            <div
              class="h-1.5 transition-all"
              :class="meterTone(meterPercent(inboxUsage))"
              :style="{ width: `${meterPercent(inboxUsage)}%` }"
            />
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-3">
          <span class="text-xs text-n-slate-10 tabular-nums hidden sm:block">
            {{
              t('BILLING.MY_PLAN.UNIT_PRICE', { price: brl(NEW_PRICES.inbox) })
            }}
          </span>
          <div
            class="flex items-center gap-1 rounded-lg border border-n-weak p-1"
          >
            <button
              type="button"
              class="i-lucide-minus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="cart.inbox <= 0"
              @click="setCart('inbox', cart.inbox - 1)"
            />
            <span
              class="w-8 text-center text-sm font-semibold text-n-slate-12 tabular-nums"
            >
              {{ cart.inbox }}
            </span>
            <button
              type="button"
              class="i-lucide-plus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              @click="setCart('inbox', cart.inbox + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Usuários -->
      <div class="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5">
            <span class="i-lucide-users w-4 h-4 text-n-slate-10 shrink-0" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BILLING.MY_PLAN.USERS') }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums ml-auto sm:ml-0">
              {{ agentUsage?.current ?? 0 }} /
              {{ agentUsage?.limit ?? t('BILLING.MY_PLAN.UNLIMITED') }}
            </span>
          </div>
          <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
            <div
              class="h-1.5 transition-all"
              :class="meterTone(meterPercent(agentUsage))"
              :style="{ width: `${meterPercent(agentUsage)}%` }"
            />
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-3">
          <span class="text-xs text-n-slate-10 tabular-nums hidden sm:block">
            {{
              t('BILLING.MY_PLAN.UNIT_PRICE', { price: brl(NEW_PRICES.agent) })
            }}
          </span>
          <div
            class="flex items-center gap-1 rounded-lg border border-n-weak p-1"
          >
            <button
              type="button"
              class="i-lucide-minus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="cart.agent <= 0"
              @click="setCart('agent', cart.agent - 1)"
            />
            <span
              class="w-8 text-center text-sm font-semibold text-n-slate-12 tabular-nums"
            >
              {{ cart.agent }}
            </span>
            <button
              type="button"
              class="i-lucide-plus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              @click="setCart('agent', cart.agent + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Disco -->
      <div class="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5">
            <span
              class="i-lucide-hard-drive w-4 h-4 text-n-slate-10 shrink-0"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BILLING.MY_PLAN.STORAGE') }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums ml-auto sm:ml-0">
              {{
                t('BILLING.MY_PLAN.STORAGE_USAGE', {
                  used: storageUsedGb,
                  limit: storageLimitGb
                    ? `${storageLimitGb} GB`
                    : t('BILLING.MY_PLAN.UNLIMITED'),
                })
              }}
            </span>
          </div>
          <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
            <div
              class="h-1.5 transition-all"
              :class="meterTone(meterPercent(storageUsage))"
              :style="{ width: `${meterPercent(storageUsage)}%` }"
            />
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-3">
          <span class="text-xs text-n-slate-10 tabular-nums hidden sm:block">
            {{
              t('BILLING.MY_PLAN.STORAGE_UNIT_PRICE', {
                price: brl(NEW_PRICES.storage5gb),
              })
            }}
          </span>
          <div
            class="flex items-center gap-1 rounded-lg border border-n-weak p-1"
            :class="{ 'opacity-50': !storageAddon }"
          >
            <button
              type="button"
              class="i-lucide-minus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="!storageAddon || cart.storage <= 0"
              @click="setCart('storage', cart.storage - 1)"
            />
            <span
              class="w-8 text-center text-sm font-semibold text-n-slate-12 tabular-nums"
            >
              {{ cart.storage }}
            </span>
            <button
              type="button"
              class="i-lucide-plus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="!storageAddon"
              :title="!storageAddon ? t('BILLING.MY_PLAN.STORAGE_SOON') : ''"
              @click="setCart('storage', cart.storage + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Contatos — pacote de +1.000 (cada unidade = +1.000 contatos por R$47) -->
      <div class="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5">
            <span class="i-lucide-contact w-4 h-4 text-n-slate-10 shrink-0" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BILLING.MY_PLAN.CONTACTS') }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums ml-auto sm:ml-0">
              {{ contactsUsage?.current ?? 0 }} /
              {{ contactsUsage?.limit ?? t('BILLING.MY_PLAN.UNLIMITED') }}
            </span>
          </div>
          <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
            <div
              class="h-1.5 transition-all"
              :class="meterTone(meterPercent(contactsUsage))"
              :style="{ width: `${meterPercent(contactsUsage)}%` }"
            />
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-3">
          <span class="text-xs text-n-slate-10 tabular-nums hidden sm:block">
            {{
              t('BILLING.MY_PLAN.CONTACTS_UNIT_PRICE', {
                price: brl(NEW_PRICES.contacts1k),
              })
            }}
          </span>
          <div
            class="flex items-center gap-1 rounded-lg border border-n-weak p-1"
            :class="{ 'opacity-50': !contactsAddon }"
          >
            <button
              type="button"
              class="i-lucide-minus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="!contactsAddon || cart.contacts <= 0"
              @click="setCart('contacts', cart.contacts - 1)"
            />
            <span
              class="w-8 text-center text-sm font-semibold text-n-slate-12 tabular-nums"
            >
              {{ cart.contacts }}
            </span>
            <button
              type="button"
              class="i-lucide-plus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="!contactsAddon"
              :title="!contactsAddon ? t('BILLING.MY_PLAN.CONTACTS_SOON') : ''"
              @click="setCart('contacts', cart.contacts + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Redes sociais (Estúdio Social) — só com o módulo ativo -->
      <div
        v-if="socialUsage"
        class="p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5">
            <span class="i-lucide-share-2 w-4 h-4 text-n-slate-10 shrink-0" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BILLING.MY_PLAN.SOCIAL_NETWORKS') }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums ml-auto sm:ml-0">
              {{ socialUsage?.current ?? 0 }} /
              {{ socialUsage?.limit ?? t('BILLING.MY_PLAN.UNLIMITED') }}
            </span>
          </div>
          <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
            <div
              class="h-1.5 transition-all"
              :class="meterTone(meterPercent(socialUsage))"
              :style="{ width: `${meterPercent(socialUsage)}%` }"
            />
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-3">
          <span class="text-xs text-n-slate-10 tabular-nums hidden sm:block">
            {{
              t('BILLING.MY_PLAN.UNIT_PRICE', {
                price: brl(socialNetworkAddon?.monthly_price || 47),
              })
            }}
          </span>
          <div
            class="flex items-center gap-1 rounded-lg border border-n-weak p-1"
          >
            <button
              type="button"
              class="i-lucide-minus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="cart.social_network <= 0"
              @click="setCart('social_network', cart.social_network - 1)"
            />
            <span
              class="w-8 text-center text-sm font-semibold text-n-slate-12 tabular-nums"
            >
              {{ cart.social_network }}
            </span>
            <button
              type="button"
              class="i-lucide-plus w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              @click="setCart('social_network', cart.social_network + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Módulos de produto (preço varia por módulo — vem do catálogo) -->
      <div class="p-5 flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <span class="i-lucide-blocks w-4 h-4 text-n-slate-10" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('BILLING.MY_PLAN.MODULES_TITLE') }}
          </span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div
            v-for="mod in MODULES"
            :key="mod.key"
            class="rounded-xl border border-n-weak p-4 flex flex-col gap-2"
          >
            <div class="flex items-center gap-2">
              <span :class="mod.icon" class="w-4 h-4 text-n-slate-10" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t(`BILLING.MY_PLAN.MODULE_${mod.key.toUpperCase()}`) }}
              </span>
            </div>
            <p class="text-xs text-n-slate-11 flex-1">
              {{ t(`BILLING.MY_PLAN.MODULE_${mod.key.toUpperCase()}_DESC`) }}
            </p>
            <span
              v-if="moduleEnabled(mod.key)"
              class="self-start text-[11px] px-2 py-0.5 rounded-full bg-n-teal-9/15 text-n-teal-11 font-medium"
            >
              {{ t(moduleBadgeKey(mod.key)) }}
            </span>
            <button
              v-else
              type="button"
              class="self-start text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              :class="
                cart[mod.key] > 0
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              :disabled="!addonFor(mod.slug)"
              :title="
                !addonFor(mod.slug)
                  ? t('BILLING.MY_PLAN.MODULE_UNAVAILABLE')
                  : ''
              "
              @click="toggleModuleInCart(mod.key)"
            >
              {{
                !addonFor(mod.slug)
                  ? t('BILLING.MY_PLAN.MODULE_UNAVAILABLE')
                  : cart[mod.key] > 0
                    ? t('BILLING.MY_PLAN.MODULE_IN_CART')
                    : t('BILLING.MY_PLAN.MODULE_ADD', {
                        price: brl(modulePriceFor(mod)),
                      })
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Resumo do carrinho + upgrade (paga tudo num PIX só) -->
      <div
        class="p-5 bg-n-alpha-1 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
      >
        <div>
          <p class="text-sm text-n-slate-11">
            {{ t('BILLING.MY_PLAN.CART_TOTAL_LABEL') }}
          </p>
          <p class="text-xl font-semibold text-n-slate-12 tabular-nums">
            {{ brl(cartTotal) }}
            <span class="text-xs font-normal text-n-slate-10">
              {{ t('BILLING.MY_PLAN.PER_MONTH') }}
            </span>
          </p>
        </div>
        <button
          type="button"
          class="rounded-lg bg-n-brand text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="cartCount === 0 || isSubmitting"
          @click="checkout"
        >
          {{
            isSubmitting
              ? t('BILLING.MY_PLAN.SAVING')
              : t('BILLING.MY_PLAN.CHECKOUT_CTA')
          }}
        </button>
      </div>
    </div>

    <!-- Modal: dados de cobrança / PIX -->
    <div
      v-if="modal.open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="closeModal"
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-n-solid-2 border border-n-weak p-6"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('BILLING.MY_PLAN.PURCHASE_TITLE') }}
          </h3>
          <button
            type="button"
            class="i-lucide-x w-4 h-4 text-n-slate-10"
            @click="closeModal"
          />
        </div>

        <!-- Passo: dados de cobrança (1ª compra) -->
        <div v-if="modal.step === 'billingInfo'" class="space-y-3">
          <p class="text-xs text-n-slate-10">
            {{ t('BILLING.MY_PLAN.NEEDS_BILLING_INFO_HINT') }}
          </p>
          <input
            v-model="billingForm.cpfCnpj"
            type="text"
            inputmode="numeric"
            :placeholder="t('BILLING.MY_PLAN.DOCUMENT_PH')"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <input
            v-model="billingForm.name"
            type="text"
            :placeholder="t('BILLING.MY_PLAN.NAME_PH')"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <input
            v-model="billingForm.phone"
            type="tel"
            :placeholder="t('BILLING.MY_PLAN.PHONE_PH')"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <button
            type="button"
            class="w-full rounded-lg bg-n-brand text-white py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            :disabled="isSavingBilling"
            @click="submitBillingInfo"
          >
            {{
              isSavingBilling
                ? t('BILLING.MY_PLAN.SAVING')
                : t('BILLING.MY_PLAN.CONTINUE_TO_PAYMENT')
            }}
          </button>
        </div>

        <!-- Passo: PIX -->
        <div v-else-if="modal.step === 'pix'" class="space-y-4">
          <p class="text-xs text-center text-n-slate-10">
            {{ t('BILLING.MY_PLAN.SCAN_QR') }}
          </p>
          <div v-if="modal.invoice?.pix_qr_code" class="flex justify-center">
            <div class="p-3 bg-white rounded-lg shadow-sm border border-n-weak">
              <img
                :src="`data:image/png;base64,${modal.invoice.pix_qr_code}`"
                :alt="t('BILLING.MY_PLAN.PIX_QR_ALT')"
                class="w-48 h-48"
              />
            </div>
          </div>
          <button
            type="button"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors"
            :class="
              pixCopied
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-brand text-white hover:opacity-90'
            "
            @click="copyPix"
          >
            <span
              :class="pixCopied ? 'i-lucide-check' : 'i-lucide-copy'"
              class="w-4 h-4"
            />
            {{
              pixCopied
                ? t('BILLING.MY_PLAN.PIX_COPIED')
                : t('BILLING.MY_PLAN.PIX_COPY')
            }}
          </button>
          <div
            class="flex items-center justify-center gap-2 text-xs text-n-slate-10"
          >
            <span class="i-lucide-loader-2 w-3.5 h-3.5 animate-spin" />
            {{ t('BILLING.MY_PLAN.PIX_WAITING') }}
          </div>
        </div>

        <!-- Passo: confirmado -->
        <div v-else class="text-center py-4">
          <span
            class="i-lucide-check-circle w-10 h-10 text-n-teal-11 mx-auto mb-2 block"
          />
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('BILLING.MY_PLAN.ACTIVATED') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
